import { HalloweenArt } from "../../../../theme/HalloweenArt";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { canBypassClosure } from "../../../../utils/access";
import { remainingOrders } from "../../../../utils/orderLimit";
import {
  OrderResponse,
  getAllHistoryUser,
  getCountInOneDay,
  getQueue,
} from "../../../../api/order.api";
import DeleteMenu from "../modal_page/confirmDeleteInOrder";
import { State } from "../../../../reducer";
import { getCafeStatus } from "../../../../api/cafe.api";
import { User } from "../../../../interfaces/user";

const TYPE_META: Record<string, { emoji: string; label: string }> = {
  hot:    { emoji: "🔥", label: "Hot" },
  cold:   { emoji: "🧊", label: "Cold" },
  frappe: { emoji: "🥤", label: "Frappe" },
  "extra-shot":  { emoji: "⚡", label: "Extra shot" },
  "reduce-shot": { emoji: "🪶", label: "Reduce shot" },
};

type OrderStatus = "ordered" | "in-progress" | "done" | "picked" | "rejected" | "cancel";

const STATUS_META: Record<OrderStatus, { label: string; tone: string }> = {
  ordered:       { label: "Waiting",     tone: "bg-ink-100 text-ink-900" },
  "in-progress": { label: "Brewing",     tone: "bg-amber-100 text-amber-900" },
  done:          { label: "Ready",       tone: "bg-emerald-100 text-emerald-900" },
  picked:        { label: "Picked up",   tone: "bg-ink-100 text-ink-500" },
  rejected:      { label: "Cancelled",   tone: "bg-ink-100 text-ink-500" },
  cancel:        { label: "Cancelled",   tone: "bg-ink-100 text-ink-500" },
};

interface OrderCardProps {
  order: OrderResponse;
  queue?: number;
  action?: () => void;
}

// Live "X minutes ago" / "X:XX" elapsed counter that ticks every second
function useElapsed(from?: string | Date | null): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!from) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [from]);
  if (!from) return null;
  const start = new Date(from).getTime();
  const seconds = Math.max(0, Math.floor((now - start) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const STEP_LABELS = ["Ordered", "Brewing", "Ready"];

const OrderCard: React.FC<OrderCardProps> = ({ action, order, queue }) => {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const status = order.status as OrderStatus;
  const meta = STATUS_META[status] ?? STATUS_META.ordered;
  const typeMeta = TYPE_META[order.type] ?? { emoji: "", label: order.type };

  const closeDelete = () => { action?.(); setIsDeleteOpen(false); };

  const showProgress = ["ordered", "in-progress", "done"].includes(status);
  const stepIndex =
    status === "ordered" ? 0 : status === "in-progress" ? 1 : status === "done" ? 2 : -1;
  const progressPct = stepIndex < 0 ? 0 : stepIndex === 0 ? 8 : stepIndex === 1 ? 50 : 100;

  // Live elapsed timer for brewing orders (uses brewedAt if backend has stamped it,
  // otherwise falls back to updatedAt).
  // @ts-ignore — backend may not have brewedAt yet on legacy rows
  const brewedAt = order.brewedAt ?? order.updatedAt;
  const brewElapsed = useElapsed(status === "in-progress" ? brewedAt : null);

  // ============= READY card — celebratory hero =============
  if (status === "done") {
    return (
      <article className="relative overflow-hidden rounded-3xl bg-hero text-hero-on p-5 mb-3 shadow-floaty animate-slide-up">
        {/* Decorative festive blobs */}
        <div
          className="absolute -right-6 -top-6 w-[140px] h-[140px] rounded-full opacity-30"
          style={{ background: "var(--festive-1, #ffffff)" }}
        />
        <div
          className="absolute -right-2 top-16 w-[80px] h-[80px] rounded-full opacity-20"
          style={{ background: "var(--festive-2, #ffffff)" }}
        />
        <div className="absolute inset-0 opacity-[0.08] bg-gradient-to-br from-white via-transparent to-transparent" />

        <div className="relative">
          <div className="flex items-start gap-4">
            <div className="text-[44px] leading-none animate-ready-bounce flex-shrink-0">
              ☕
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold tracking-[0.12em] uppercase text-white/60">
                Ready for pickup
              </p>
              <h3 className="font-display font-black text-[26px] leading-tight tracking-tight mt-1 break-words">
                {order.menu.name}
              </h3>
              <p className="text-sm text-white/70 mt-1">
                {typeMeta.emoji} {typeMeta.label}
                {order.menu.category.categoryFor === "cafe" && ` · ${order.sweetness}% sweet`}
              </p>
            </div>
          </div>

          <p className="mt-5 text-center text-sm text-white/70">
            Show this screen at the bar — the barista will mark it picked up.
          </p>
        </div>

        <DeleteMenu isOpen={isDeleteOpen} onClose={closeDelete} deleteID={order.id} action={action} />
      </article>
    );
  }

  // ============= Standard card (ordered / in-progress / picked / cancelled) =============
  return (
    <>
      <DeleteMenu isOpen={isDeleteOpen} onClose={closeDelete} deleteID={order.id} action={action} />
      <article className="card-v2 p-4 mb-3 animate-slide-up">
        <div className="flex gap-3">
          {/* Tumbler thumb (only for active cafe orders) */}
          {showProgress && order.menu.category.categoryFor === "cafe" && order.tumblerImage && (
            <div className="flex-shrink-0 relative">
              <img
                src={order.tumblerImage}
                alt="tumbler"
                className="w-[64px] h-[64px] object-cover rounded-2xl"
              />
              {status === "in-progress" && (
                <span className="absolute -top-1 -right-1 text-lg animate-steam">♨️</span>
              )}
            </div>
          )}

          <div className="flex-1 min-w-0">
            {/* Top row: time + status pill */}
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-ink-500 font-medium">
                {moment(showProgress ? order.orderDate : order.updatedAt).format("HH:mm")}
              </span>
              <div className="flex items-center gap-2">
                {status === "ordered" && queue !== undefined && (
                  <span className="text-xs font-semibold text-ink-1000">
                    {queue && queue > 1 ? `Queue #${queue - 1}` : "You're next!"}
                  </span>
                )}
                {status === "in-progress" && brewElapsed && (
                  <span className="text-xs font-semibold text-amber-700 inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    {brewElapsed}
                  </span>
                )}
                <span className={`px-2.5 h-[24px] inline-flex items-center rounded-full text-[11px] font-bold ${meta.tone}`}>
                  {meta.label}
                </span>
              </div>
            </div>

            {/* Drink name */}
            <h3 className="font-display font-black text-[18px] tracking-tight text-ink-1000 break-words leading-tight">
              {order.menu.name}
            </h3>

            {/* Meta line */}
            <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1 text-xs text-ink-500">
              <span>{typeMeta.emoji} {typeMeta.label}</span>
              {order.menu.category.categoryFor === "cafe" && (
                <>
                  <span className="text-ink-300">·</span>
                  <span>{order.sweetness}% sweet</span>
                </>
              )}
              {order.remark && order.remark !== "-" && (
                <>
                  <span className="text-ink-300">·</span>
                  <span className="italic truncate">"{order.remark}"</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Cancelled-by attribution */}
        {(status === "cancel" || status === "rejected") && (() => {
          // @ts-ignore — backend now returns cancelledBy on order
          const by = order.cancelledBy as { id: number; name: string; role?: string } | null | undefined;
          const ownerId = (order.user as { id?: number } | undefined)?.id;
          const text = by
            ? by.id === ownerId
              ? "Cancelled by you"
              : `Cancelled by ${by.name}${by.role && by.role !== "user" ? ` (${by.role})` : ""}`
            : status === "rejected" ? "Rejected by barista" : null;
          if (!text) return null;
          return <p className="mt-2 text-xs text-ink-500">{text}</p>;
        })()}

        {/* Reject note */}
        {order.rejectNote && (
          <div className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-900">
            {order.rejectNote}
          </div>
        )}

        {/* Animated progress bar with step dots */}
        {showProgress && (
          <div className="mt-4 pt-3 border-t border-ink-100">
            <div className="relative h-[28px]">
              {/* Track */}
              <div className="absolute top-[13px] left-0 right-0 h-[3px] rounded-full bg-ink-100" />
              {/* Fill */}
              <div
                className="absolute top-[13px] left-0 h-[3px] rounded-full bg-accent transition-all duration-700 ease-out"
                style={{ width: `${progressPct}%` }}
              />
              {/* Brewing shimmer */}
              {status === "in-progress" && (
                <div
                  className="absolute top-[13px] h-[3px] rounded-full pointer-events-none"
                  style={{
                    left: 0,
                    width: `${progressPct}%`,
                    backgroundImage:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.6s linear infinite",
                  }}
                />
              )}
              {/* Step dots */}
              <div className="absolute inset-0 flex justify-between">
                {STEP_LABELS.map((_, i) => {
                  const reached = i <= stepIndex;
                  const isCurrent = i === stepIndex;
                  return (
                    <div
                      key={i}
                      className={`w-[28px] h-[28px] rounded-full flex items-center justify-center transition-all ${
                        reached ? "bg-accent text-accent-on" : "bg-white border-2 border-ink-100 text-ink-400"
                      } ${isCurrent ? "animate-pulse-ring" : ""}`}
                    >
                      {reached ? (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                        </svg>
                      ) : (
                        <span className="text-[11px] font-bold">{i + 1}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-between mt-2">
              {STEP_LABELS.map((label, i) => (
                <span
                  key={label}
                  className={`text-[10px] font-semibold ${i <= stepIndex ? "text-ink-1000" : "text-ink-400"}`}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Cancel action */}
        {status === "ordered" && (
          <button
            type="button"
            onClick={() => setIsDeleteOpen(true)}
            className="btn-v2-secondary btn-v2-md w-full mt-4"
          >
            Cancel order
          </button>
        )}
      </article>
    </>
  );
};

export default function StatusOrderCard() {
  const user = useSelector((state: State) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [orderRemaining, setOrderRemaining] = useState(2);
  const [currentQueue, setCurrentQueue] = useState();
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === "admin" || user?.role === "dev";
  const menuPath = isAdmin ? "/menuCafe-admin" : "/menuCafe";

  const storeRudux = (response: User) => dispatch({ type: "LOGIN", payload: response });

  const fetchOrderedData = async () => {
    setLoading(true);
    try {
      const data = await getAllHistoryUser();
      // @ts-ignore
      setData(data);
    } catch {} finally { setLoading(false); }
  };

  const getOrderCount = async () => {
    try {
      const c = await getCountInOneDay();
      setOrderRemaining(remainingOrders(c));
    } catch {}
  };

  const fetchQueue = async () => {
    try {
      const res = await getQueue();
      setCurrentQueue(res);
    } catch {}
  };

  const afterAction = () => {
    fetchOrderedData();
    getOrderCount();
  };

  useEffect(() => {
    getOrderCount();
    fetchQueue();
    fetchOrderedData();
    getCafeStatus().then((result) => {
      const responsepost: User = {
        login: true, sub: "auth_google",
        id: user?.id, email: user?.email, name: user?.name, role: user?.role,
        isBarOpen: result?.isBarOpen, isCafeOpen: result?.isCafeOpen,
      };
      const bypass = canBypassClosure(user?.role);
      if (!result?.isCafeOpen && !result?.isBarOpen && !bypass) {
        return navigate(isAdmin ? "/close-all-admin" : "/close-all");
      } else if (!result?.isCafeOpen && result?.isBarOpen && !bypass) {
        return navigate("/order_bar");
      }
      storeRudux(responsepost);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const todayItems = useMemo(() => {
    if (!data) return [] as OrderResponse[];
    const today = new Date().toISOString().split("T")[0];
    // @ts-ignore
    return (data as OrderResponse[]).filter((e) => e.orderDate.split("T")[0] === today);
  }, [data]);

  const activeOrders = useMemo(
    () => todayItems
      .filter((e) => !["picked", "rejected", "cancel"].includes(e.status))
      // @ts-ignore
      .sort((a, b) => new Date(a.orderDate) - new Date(b.orderDate)),
    [todayItems]
  );

  const finishedOrders = useMemo(
    () => todayItems
      .filter((e) => ["picked", "rejected", "cancel"].includes(e.status))
      // @ts-ignore
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
    [todayItems]
  );

  const isDessert = (o: OrderResponse) => o.menu?.category?.categoryFor === "dessert";
  const activeDrinks    = useMemo(() => activeOrders.filter((o) => !isDessert(o)),  [activeOrders]);
  const activeDesserts  = useMemo(() => activeOrders.filter((o) =>  isDessert(o)),  [activeOrders]);
  const finishedDrinks  = useMemo(() => finishedOrders.filter((o) => !isDessert(o)), [finishedOrders]);
  const finishedDesserts= useMemo(() => finishedOrders.filter((o) =>  isDessert(o)), [finishedOrders]);

  // Most recent successfully-placed order (any day) for the "Reorder last"
  // shortcut. Cancelled and rejected orders never happened, so skip them.
  const lastOrder = useMemo(() => {
    if (!data) return null;
    return (data as OrderResponse[])
      .filter((o) => !["cancel", "rejected"].includes(o.status))
      // @ts-ignore — Date arithmetic via numeric coercion is fine
      .sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate))[0] ?? null;
  }, [data]);

  const handleReorder = () => {
    if (!lastOrder) return;
    dispatch({
      type: "SETMENU",
      payload: {
        menu_id: lastOrder.menu.id,
        menu_name: lastOrder.menu.name,
        type: lastOrder.type,
        sweetness: lastOrder.sweetness ?? 100,
        remark: lastOrder.remark && lastOrder.remark !== "-" ? lastOrder.remark : "",
        tumblerImage: lastOrder.tumblerImage ?? "",
      },
    });
    const isDess = isDessert(lastOrder);
    const base = isDess
      ? (isAdmin ? "form-dessert-admin" : "form-dessert")
      : (isAdmin ? "form-admin" : "form");
    navigate(`/${base}/${lastOrder.menu.name}/${lastOrder.menu.id}`);
  };

  // Look up live queue position by order_id (robust to FE-side filtering / re-grouping).
  const queueAtFor = (orderId: number): number => {
    if (!currentQueue) return 0;
    // @ts-ignore
    const hit = (currentQueue as Array<{ order_id: number; queue_at: number }>).find((q) => q.order_id === orderId);
    return hit?.queue_at ?? 0;
  };

  return (
    <div className="min-h-screen bg-white font-sans pb-[100px]">
      {/* Hero */}
      <header className="px-5 pt-[24px] pb-[20px]">
        <HalloweenArt variant="lantern" className="art-heading" />
        <p className="eyebrow-v2 mb-2">My queue</p>
        <h1 className="font-display font-black text-[30px] leading-[1.1] tracking-tightest text-ink-1000">
          {activeOrders.length > 0
            ? `You have ${activeOrders.length} order${activeOrders.length > 1 ? "s" : ""} in progress`
            : "Nothing in your queue"}
        </h1>
        <p className="text-sm text-ink-500 mt-2">
          {activeDrinks.length > 0 && (
            <>{activeDrinks.length} drink{activeDrinks.length > 1 ? "s" : ""}</>
          )}
          {activeDrinks.length > 0 && activeDesserts.length > 0 && <span> · </span>}
          {activeDesserts.length > 0 && (
            <>{activeDesserts.length} dessert{activeDesserts.length > 1 ? "s" : ""}</>
          )}
          {activeOrders.length === 0 && (
            orderRemaining > 0
              ? `${orderRemaining} order${orderRemaining > 1 ? "s" : ""} left today`
              : "Daily limit reached"
          )}
        </p>
      </header>

      <div className="border-b border-ink-100" />

      {/* Reorder last — quick path back to the customise screen with the
          previous order prefilled. Hidden when the user has no prior
          successful order to copy or has used up the day's slots. */}
      {!loading && lastOrder && orderRemaining > 0 && (
        <div className="px-5 pt-4">
          <button
            type="button"
            onClick={handleReorder}
            className="w-full flex items-center gap-3 p-3.5 rounded-2xl bg-ink-50 hover:bg-ink-100 active:scale-[0.98] transition text-left"
          >
            <span className="w-11 h-11 rounded-full bg-ink-1000 text-ink-0 flex items-center justify-center text-xl shrink-0" aria-hidden>
              ↻
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-ink-500">Reorder last</p>
              <p className="font-display font-bold text-ink-1000 truncate">{lastOrder.menu.name}</p>
              <p className="text-xs text-ink-500 truncate">
                {(TYPE_META[lastOrder.type]?.emoji ?? "") + " " + (TYPE_META[lastOrder.type]?.label ?? lastOrder.type)}
                {lastOrder.menu.category.categoryFor === "cafe" && ` · ${lastOrder.sweetness}% sweet`}
              </p>
            </div>
            <svg className="w-5 h-5 text-ink-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      <main className="px-5 pt-[20px]">
        {loading ? (
          <div className="py-[80px] flex justify-center">
            <svg className="w-6 h-6 animate-spin text-ink-700" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
              <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            </svg>
          </div>
        ) : todayItems.length === 0 ? (
          <div className="py-[64px] flex flex-col items-center text-center">
            <div className="seasonal-empty-icon w-[64px] h-[64px] rounded-full bg-ink-100 flex items-center justify-center mb-4 text-3xl">
              ☕
            </div>
            <HalloweenArt variant="ghost-coffee" className="art-empty" />
            <p className="font-display font-black text-lg text-ink-1000">Nothing here yet</p>
            <p className="text-sm text-ink-500 mt-1">Place your first order today</p>
            <button
              type="button"
              onClick={() => navigate(menuPath)}
              className="btn-v2-primary btn-v2-lg mt-6"
            >
              Order now
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
              </svg>
            </button>
          </div>
        ) : (
          <>
            {activeDrinks.length > 0 && (
              <section>
                <p className="eyebrow-v2 mb-3 flex items-center gap-1.5">
                  <span aria-hidden>☕</span>
                  Drinks · in progress
                </p>
                {activeDrinks.map((order) => (
                  <OrderCard
                    key={`order-${order.id}`}
                    order={order}
                    action={afterAction}
                    queue={queueAtFor(order.id)}
                  />
                ))}
              </section>
            )}

            {activeDesserts.length > 0 && (
              <section className={activeDrinks.length > 0 ? "mt-8" : ""}>
                <p className="eyebrow-v2 mb-3 flex items-center gap-1.5">
                  <span aria-hidden>🍰</span>
                  Desserts · in progress
                </p>
                {activeDesserts.map((order) => (
                  <OrderCard
                    key={`order-${order.id}`}
                    order={order}
                    action={afterAction}
                    queue={queueAtFor(order.id)}
                  />
                ))}
              </section>
            )}

            {finishedDrinks.length > 0 && (
              <section className="mt-8">
                <p className="eyebrow-v2 mb-3 flex items-center gap-1.5">
                  <span aria-hidden>☕</span>
                  Drinks · finished today
                </p>
                {finishedDrinks.map((order) => (
                  <OrderCard key={`order-${order.id}`} order={order} />
                ))}
              </section>
            )}

            {finishedDesserts.length > 0 && (
              <section className="mt-8">
                <p className="eyebrow-v2 mb-3 flex items-center gap-1.5">
                  <span aria-hidden>🍰</span>
                  Desserts · finished today
                </p>
                {finishedDesserts.map((order) => (
                  <OrderCard key={`order-${order.id}`} order={order} />
                ))}
              </section>
            )}
          </>
        )}
      </main>

      {/* Floating CTA — only when there's slot remaining */}
      {!loading && orderRemaining > 0 && (
        <button
          type="button"
          onClick={() => navigate(menuPath)}
          className="fab-v2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/>
          </svg>
          New order
        </button>
      )}
    </div>
  );
}
